import type { ConnectionState } from "./types"
import { ConnectionError } from "./errors"

export interface ConnectionConfig {
  url: string
  ticket?: string
  autoReconnect?: boolean
  reconnectInterval?: number
  reconnectMaxAttempts?: number
  enableHeartbeat?: boolean
}

interface PendingMessage {
  data: string
  resolve: (value: void) => void
  reject: (reason?: unknown) => void
}

export class ConnectionManager {
  private ws: WebSocket | null = null
  private state: ConnectionState = "disconnected"
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private pendingMessages: PendingMessage[] = []
  private messageQueue: string[] = []
  private stateChangeHandlers: Set<(state: ConnectionState) => void> = new Set()
  private messageHandlers: Set<(data: string) => void> = new Set()
  private manualDisconnect = false
  private intentionallyClosed: WeakSet<WebSocket> = new WeakSet()
  private socketGeneration = 0

  /**
   * Track pending connect operation for safe disconnect during mid-connect race.
   * If disconnect() is called while connect() is in progress, we reject the connect promise
   * before reassigning event handlers, preventing hung promises in StrictMode cleanup.
   */
  private pendingConnect: { resolve: () => void; reject: (err: Error) => void } | null = null

  constructor(private config: ConnectionConfig) {}

  connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }

    if (this.pendingConnect) {
      return new Promise((resolve, reject) => {
        const pending = this.pendingConnect!
        const origResolve = pending.resolve
        const origReject = pending.reject
        pending.resolve = () => {
          origResolve()
          resolve()
        }
        pending.reject = (err: Error) => {
          origReject(err)
          reject(err)
        }
      })
    }

    return new Promise((resolve, reject) => {
      this.manualDisconnect = false

      /**
       * Track pending connect operation.
       * If disconnect() is called mid-flight, we use this reference to reject
       * the connect promise before reassigning event handlers.
       */
      this.pendingConnect = { resolve, reject }

      this.setState("connecting")

      try {
        const url = this.config.ticket
          ? `${this.config.url}?ticket=${encodeURIComponent(this.config.ticket)}`
          : this.config.url

        this.ws = new WebSocket(url)
        const currentSocket = this.ws
        const socketGen = ++this.socketGeneration

        this.ws.onopen = () => {
          if (currentSocket !== this.ws) return
          const pending = this.pendingConnect
          this.pendingConnect = null
          this.reconnectAttempts = 0
          this.setState("connected")
          this.processMessageQueue()
          if (this.config.enableHeartbeat) {
            this.startHeartbeat()
          }
          if (pending) {
            pending.resolve()
          }
        }

        this.ws.onerror = () => {
          if (currentSocket !== this.ws) return
          const pending = this.pendingConnect
          this.pendingConnect = null
          this.setState("error")
          if (pending) {
            pending.reject(new ConnectionError("WebSocket connection error"))
          }
        }

        this.ws.onclose = () => {
          if (socketGen !== this.socketGeneration) return
          const pending = this.pendingConnect
          this.pendingConnect = null
          this.setState("disconnected")
          this.stopHeartbeat()

          if (!this.manualDisconnect && this.config.autoReconnect && this.reconnectAttempts < (this.config.reconnectMaxAttempts || 5)) {
            this.scheduleReconnect()
            if (pending) {
              pending.reject(new ConnectionError("Connection closed, attempting reconnect"))
            }
          } else {
            this.rejectPendingMessages(new ConnectionError("WebSocket closed"))
            if (pending) {
              pending.reject(new ConnectionError("WebSocket closed"))
            }
          }
        }

        this.ws.onmessage = (event) => {
          if (currentSocket !== this.ws) return
          this.messageHandlers.forEach((handler) => handler(event.data))
        }
      } catch (error) {
        const pending = this.pendingConnect
        this.pendingConnect = null
        this.setState("error")
        if (pending) {
          pending.reject(new ConnectionError("Failed to create WebSocket connection"))
        }
      }
    })
  }

  disconnect(): Promise<void> {
    return new Promise((resolve) => {
      this.manualDisconnect = true
      this.stopHeartbeat()

      /**
       * Reject any pending connect operation before disconnecting.
       * This prevents the connect promise from hanging if disconnect() is called
       * while connect() is in progress (race condition during StrictMode cleanup).
       */
      if (this.pendingConnect) {
        this.pendingConnect.reject(new ConnectionError("Connection cancelled during disconnect"))
        this.pendingConnect = null
      }

      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.rejectPendingMessages(new ConnectionError("Connection cancelled"))
      }

      if (this.ws) {
        const closingSocket = this.ws
        const closingGen = this.socketGeneration
        this.intentionallyClosed.add(closingSocket)
        this.ws.onopen = null
        this.ws.onerror = null
        this.ws.onmessage = null
        this.ws.onclose = () => {
          if (!this.intentionallyClosed.has(closingSocket)) {
            resolve()
            return
          }
          if (closingGen === this.socketGeneration) {
            this.setState("disconnected")
          }
          resolve()
        }
        this.ws.close()
        this.ws = null
      } else {
        this.setState("disconnected")
        resolve()
      }
    })
  }

  send(data: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(data)
          resolve()
        } catch (error) {
          reject(new ConnectionError("Failed to send message"))
        }
      } else {
        if (this.config.autoReconnect && this.state === "disconnected") {
          this.messageQueue.push(data)
          this.connect().then(resolve).catch(reject)
        } else {
          reject(new ConnectionError("WebSocket is not connected"))
        }
      }
    })
  }

  onMessage(handler: (data: string) => void): () => void {
    this.messageHandlers.add(handler)
    return () => this.messageHandlers.delete(handler)
  }

  onStateChange(handler: (state: ConnectionState) => void): () => void {
    this.stateChangeHandlers.add(handler)
    return () => this.stateChangeHandlers.delete(handler)
  }

  getConnectionState(): ConnectionState {
    return this.state
  }

  setAutoReconnect(enabled: boolean): void {
    this.config.autoReconnect = enabled
  }

  private setState(state: ConnectionState): void {
    this.state = state
    this.stateChangeHandlers.forEach((handler) => {
      try {
        handler(state)
      } catch (error) {
        console.error("Error in state change handler:", error)
      }
    })
  }

  private scheduleReconnect(): void {
    this.setState("reconnecting")
    const interval = this.config.reconnectInterval || 1000
    const delay = interval * Math.pow(2, this.reconnectAttempts)

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++
      this.connect().catch(() => {
      })
    }, delay)
  }

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift()!
      try {
        this.ws.send(message)
      } catch (error) {
        console.error("Failed to send queued message:", error)
      }
    }
  }

  private startHeartbeat(): void {
    // Server uses native WebSocket ping/pong frames; no app-level heartbeat needed.
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private rejectPendingMessages(error: Error): void {
    this.pendingMessages.forEach((pending) => {
      pending.reject(error)
    })
    this.pendingMessages = []
  }
}
